import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Typography, styled, TextField, Paper } from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import { useAuth } from "../../context/authContext";

const LoginTextField = styled(TextField)({
  backgroundColor: "white",
  borderRadius: "5px 5px 0 0",
});

const LoginForm: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/previsions");
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      await login(username, password);
      navigate("/previsions");
    } catch (err: any) {
      console.error("Erreur de connexion:", err);
      
      let message = "Erreur de connexion. Veuillez réessayer.";
      let details = "";

      if (err?.message) {
        if (err.message.toLowerCase().includes("invalid") || 
            err.message.toLowerCase().includes("incorrect")) {
          message = "Identifiant ou mot de passe incorrect.";
          details = "Veuillez vérifier vos informations de connexion.";
        } else {
          details = err.message;
        }
      } else if (err?.detail) {
        details = err.detail;
      } else if (err?.response?.data?.message) {
        details = err.response.data.message;
      } else if (err?.response?.status === 401) {
        message = "Identifiant ou mot de passe incorrect.";
        details = "Veuillez vérifier vos informations de connexion.";
      } else if (err?.response?.status === 502) {
        message = "Erreur de connexion au serveur d'authentification.";
        details = "Le serveur Keycloak est peut-être indisponible.";
      } else if (err?.response?.status >= 500) {
        message = "Erreur serveur.";
        details = "Le serveur rencontre un problème. Veuillez réessayer plus tard.";
      }

      const fullMessage = details ? `${message}\n${details}` : message;
      setError(fullMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={6}
      sx={{
  width: { xs: "100%", sm: 294, md: 336 },
        height: "100vh",
        py: { xs: 4, md: 6 },
        px: { xs: 3, md: 4 },
        borderRadius: 0,
        backdropFilter: "blur(2px)",
        backgroundColor: "rgba(255,255,255,0.88)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
      }}
    >
      <img src="img/logo.png" style={{ maxWidth: 180 }} alt="Logo AVT" />
      <Typography variant="h6" align="center">
        Plateforme de statistiques d'occupation AVT
      </Typography>
      <form
        onSubmit={handleSubmit}
        style={{ width: "100%", display: "flex", flexDirection: "column", gap: 16 }}
      >
        <LoginTextField
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          size="small"
          variant="filled"
          label="Identifiant"
          autoComplete="username"
          disabled={loading}
          required
          fullWidth
        />
        <LoginTextField
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          size="small"
          variant="filled"
          label="Mot de passe"
          type="password"
          autoComplete="current-password"
          disabled={loading}
          required
          fullWidth
        />
        <Button type="submit" variant="contained" endIcon={<LoginIcon />} disabled={loading} fullWidth>
          {loading ? "Connexion en cours..." : "Connexion"}
        </Button>
        {error && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: "#ffebee",
              border: "1px solid #ef5350",
              borderRadius: 1,
            }}
          >
            <Typography color="error" variant="body2" sx={{ whiteSpace: "pre-line" }}>
              {error}
            </Typography>
          </Paper>
        )}
      </form>
    </Paper>
  );
};

export default LoginForm;
